import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import CircularProgress from "@mui/material/CircularProgress";
import EditIcon from "@mui/icons-material/Edit";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";
import MDBadge from "components/MDBadge";
import { useUsers, useUpdateUser } from "api/useUsers.ts";
import { IconButton } from "@mui/material";

export default function UsersTableData() {
  const { t } = useTranslation();
  const { data: usersData, isLoading } = useUsers();
  const users = usersData || [];
  const updateUser = useUpdateUser();
  const navigate = useNavigate();
  const [activeCount, setActiveCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [blockedCount, setBlockedCount] = useState(0);
  const [testCount, setTestCount] = useState(0);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editedUser, setEditedUser] = useState(null);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleCloseNotification = () => setNotification((prev) => ({ ...prev, open: false }));

  useEffect(() => {
    if (users) {
      setActiveCount(users.filter((user) => user.state === "Active").length);
      setPendingCount(users.filter((user) => user.state === "Pending").length);
      setBlockedCount(users.filter((user) => user.state === "Blocked").length);
      setTestCount(users.filter((user) => user.state === "Test").length);
    }
  }, [users]);

  useEffect(() => {
    if (users) {
    }
  }, [users]);

  const handleEditClick = (user) => {
    setEditedUser({ ...user });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditedUser(null);
  };

  const handleSaveChanges = async () => {
    try {
      if (!editedUser?.state || !editedUser?.role) {
        setNotification({ open: true, message: t("users.noChanges"), severity: "info" });
        return;
      }

      await updateUser.mutateAsync({
        id: editedUser._id,
        state: editedUser.state,
        role: editedUser.role,
      });
      setNotification({ open: true, message: t("users.userUpdated"), severity: "success" });
      setIsModalOpen(false);
    } catch (error) {
      setNotification({ open: true, message: t("users.userUpdateFailed"), severity: "error" });
      if (error.response?.status === 401) navigate("/authentication/sign-in");
    }
  };

  const Author = ({ image, name, email }) => (
    <MDBox display="flex" alignItems="center" flexDirection="row-reverse">
      {/* <MDAvatar src={image} name={name} size="sm" /> */}
      <MDBox mr={2} display="flex" flexDirection="column" textAlign="flex-end">
        <MDTypography variant="button" fontWeight="medium">
          {name}
        </MDTypography>
        <MDTypography variant="caption">{email}</MDTypography>
      </MDBox>
    </MDBox>
  );

  Author.propTypes = {
    image: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
  };

  const rows = useMemo(() => {
    if (isLoading) return [{ userId: t("common.loading"), User: <CircularProgress size={24} /> }];

    return users.map((user) => ({
      key: user._id,
      userId: user._id,
      User: (
        <Author
          name={user.name}
          email={user.email}
          image={`https://ui-avatars.com/api/?name=${encodeURIComponent(user.name)}`}
        />
      ),
      phoneNumber: user.phone,
      email: user.email,
      role: (
        <MDBadge
          badgeContent={user.role}
          color={
            user.role === "Admin"
              ? "info"
              : user.role === "Agent"
              ? "success"
              : user.role === "Operator"
              ? "warning"
              : "secondary" // Default color for Customer
          }
          sx={{ textAlign: "center" }}
        />
      ),
      state: (
        <MDBadge
          badgeContent={user.state}
          color={
            user.state === "Active"
              ? "success"
              : user.state === "Pending"
              ? "warning"
              : user.state === "Blocked"
              ? "error"
              : "info"
          }
          sx={{ textAlign: "center" }}
        />
      ),
      action: (
        <IconButton
          color="primary"
          size="medium"
          onClick={() => {
            handleEditClick(user);
          }}
          sx={{ textAlign: "center" }}
        >
          <EditIcon fontSize="medium" />
        </IconButton>
      ),
    }));
  }, [isLoading, users]);

  return {
    columns: [
      { Header: t("users.columns.user"), accessor: "User", width: "35%", align: "right" },
      { Header: t("users.columns.userId"), accessor: "userId", width: "15%", align: "center" },
      { Header: t("users.columns.phoneNumber"), accessor: "phoneNumber", align: "right" },
      { Header: t("users.columns.email"), accessor: "email", align: "right" },
      { Header: t("users.columns.role"), accessor: "role", align: "center" },
      { Header: t("users.columns.state"), accessor: "state", align: "center" },
      { Header: t("users.columns.action"), accessor: "action", align: "center" },
    ],
    rows,
    activeCount,
    pendingCount,
    testCount,
    blockedCount,
    isModalOpen,
    handleCloseModal,
    handleSaveChanges,
    editedUser,
    setEditedUser,
    notification,
    handleCloseNotification,
  };
}
